import { findWorldMapModel } from "@/api/beetle";
import { WorldMapData } from "@/types/worldmap";
import { useEffect, useRef, useState } from "react";
import { useDebouncedEffect, useDeepCompareEffect } from "../google/utility-hooks";
import { Map2DBounds } from "../google/google-map-2D";
import { MapImageMarker } from "./image-marker";
import { VenueMarker } from "./venue-marker";
import { Cluster, MarkerClusterer, Renderer } from "@googlemaps/markerclusterer";
import { MapVideoMarker } from "./video-marker";
import { getSupportedVenueData } from "@/types/supported-venue";

let globalUserId = "";

declare global {
  interface Window {
    FlutterAPI: {
      setUserId: (arg: string) => void;
      filterMarkers: (venue: boolean, communityMarker: boolean, myMarker: boolean) => void;
      setInitLocation: (lat: number, lng: number) => void;
    };
    FlutterBridge: {
      postMessage: (payload: string) => void;
    };
  }
}

window.FlutterAPI = {
  setUserId: (userId: string) => {
    console.log("FlutterAPI.setUserId:", userId);
    globalUserId = userId;
  },
  filterMarkers: (venue: boolean, communityMarker: boolean, myMarker: boolean) => {
    console.log("FlutterAPI.venue:", venue, "communityMarker", communityMarker, "myMarker", myMarker);

    const event = new CustomEvent("flutterFilterUpdate", {
      detail: { venue, communityMarker, myMarker }
    });
    window.dispatchEvent(event);
  },
  setInitLocation: (lat: number, lng: number) => {
    const event = new CustomEvent("flutterInitLocationUpdate", {
      detail: { lat, lng }
    });
    window.dispatchEvent(event);
  }
};

export const BeetleMarker = ({ bounds, map }: { bounds?: Map2DBounds, map?: google.maps.Map | null }) => {
  const [smallModelData, setSmallModelData] = useState<WorldMapData[] | null>(null);
  const [filters, setFilters] = useState({ venue: true, comm: true, my: true });

  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<{ [key: string]: google.maps.marker.AdvancedMarkerElement }>({});
  const smallDataRef = useRef<WorldMapData[] | null>(null);

  const venueClusterRef = useRef<MarkerClusterer | null>(null);
  const venueMarkerRef = useRef<{ [key: string]: google.maps.marker.AdvancedMarkerElement }>({});


  useEffect(() => {
    if (!map) return;

    if (!clustererRef.current) {
      const renderer: Renderer = {
        render({ count, position, markers }: Cluster): google.maps.Marker {
          const hasMyMarker = (markers ?? []).some(
            (marker) => {
              const m = marker as google.maps.marker.AdvancedMarkerElement;
              return m.dataset?.isMyMarker === "true";
            }
          );

          return new google.maps.Marker({
            position,
            icon: {
              url: hasMyMarker ? "/assets/icon_my_marker.svg" : "/assets/icon_comm_marker.svg",
              scaledSize: new google.maps.Size(24, 24),
            },
            zIndex: 600,
          });
        },
      };
      clustererRef.current = new MarkerClusterer({
        map: map as any,
        renderer,
        onClusterClick: (event, cluster, map) => {
          const clusterMarkers = cluster.markers as google.maps.marker.AdvancedMarkerElement[];
          console.log("Markers in this cluster:", clusterMarkers);

          const sessionIds = clusterMarkers.map(marker => marker.title)
          const filteredData = smallDataRef.current?.filter(data => sessionIds.includes(data.id)) || [];
          const data = filteredData.map(data => ({
            sessionId: data.id,
            myMarker: data.isMyModel,
            thumbnail: data.modelThumbnail
          }));

          console.log("smallModelData", smallDataRef.current)
          console.log("data", data)
          sendToFlutter({ type: 'marker_cluster', cluster: data });
        }
      });
      console.log("set clustererRef")
    }

    if (!venueClusterRef.current) {
      const renderer = {
        render({ count, position }: { count: any, position: any }) {
          return new google.maps.Marker({
            position,
            icon: {
              url: "/assets/icon_venue_marker.svg",
              scaledSize: new google.maps.Size(24, 24),
            },
            zIndex: 600,
          });
        },
      };
      venueClusterRef.current = new MarkerClusterer({
        map: map as any,
        renderer,
        onClusterClick: (event, cluster, map) => {
          const clusterMarkers = cluster.markers as google.maps.marker.AdvancedMarkerElement[];
          console.log("Markers in this cluster:", clusterMarkers);
          const rawVenueData = getSupportedVenueData();
          const venueCode = clusterMarkers.map(marker => marker.title)
          const filteredData = rawVenueData.filter(data => venueCode.includes(data.features[0].properties.code)) || [];
          const data = filteredData.map(data => ({
            venueCode: data.features[0].properties.code,
            thumbnail: data.features[0].properties.modelPreviewThumbnail,
            venueName: data.features[0].properties.modelName
          }));

          console.log("venueCode", data)
          sendToFlutter({ type: 'venue_cluster', cluster: data });
        }
      });
    }

    return () => {
      // Cleanup on unmount
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }

      if (venueClusterRef.current) {
        venueClusterRef.current.clearMarkers();
      }
    };
  }, [map]);

  // whenever the set of marker elements changes, re-sync the clusterer
  useEffect(() => {
    const clusterer = clustererRef.current;
    if (!clusterer) return;
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markersRef.current));
  }, [Object.keys(markersRef.current).length, smallModelData]);

  useEffect(() => {
    const clusterer = venueClusterRef.current;
    if (!clusterer) return;
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(venueMarkerRef.current));
  }, [Object.keys(venueMarkerRef.current).length, filters.venue]);


  useDeepCompareEffect(() => {
    if (smallModelData) {
      smallDataRef.current = smallModelData
    }
  }, [smallModelData])

  const handleMarkerRef = (marker: google.maps.marker.AdvancedMarkerElement | null, id: string) => {
    if (marker && !markersRef.current[id]) {
      markersRef.current[id] = marker;
    } else if (!marker && markersRef.current[id]) {
      delete markersRef.current[id];
    }
  };

  const handleVenueMarkerRef = (marker: google.maps.marker.AdvancedMarkerElement | null, id: string) => {
    if (marker && !venueMarkerRef.current[id]) {
      venueMarkerRef.current[id] = marker;
    } else if (!marker && venueMarkerRef.current[id]) {
      delete venueMarkerRef.current[id];
    }
  };

  useDebouncedEffect(() => {
    console.log("bounds changed", bounds);
    if (bounds && globalUserId) {
      retrieveSmallModel(globalUserId, `${bounds.northeast.lng()},${bounds.northeast.lat()},${bounds.southwest.lng()},${bounds.southwest.lat()}`, bounds.zoomLevel)
    }
  }, 500, [bounds])

  // force a re-render of clusters when the map zooms or bounds change
  useEffect(() => {
    if (!map) return;
    const onZoom = () => {
      clustererRef.current?.render();
      venueClusterRef.current?.render();
    };
    const onBounds = () => {
      clustererRef.current?.render();
      venueClusterRef.current?.render();
    };
    const zoomListener = map.addListener("zoom_changed", onZoom);
    const boundsListener = map.addListener("bounds_changed", onBounds);
    return () => {
      google.maps.event.removeListener(zoomListener);
      google.maps.event.removeListener(boundsListener);
    };
  }, [map]);

  useEffect(() => {
    const handleFlutterUpdate = (event: any) => {
      const { venue, communityMarker, myMarker } = event.detail;
      console.log("handleFlutterUpdate", venue, communityMarker, myMarker);
      setFilters({ venue, comm: communityMarker, my: myMarker });
    };

    window.addEventListener("flutterFilterUpdate", handleFlutterUpdate);
    return () => {
      window.removeEventListener("flutterFilterUpdate", handleFlutterUpdate);
    }
  }, []);

  useDeepCompareEffect(() => {
    console.log("filters bounds changed", bounds, "globalUserId", globalUserId);
    if (bounds && globalUserId) {
      retrieveSmallModel(globalUserId, `${bounds.northeast.lng()},${bounds.northeast.lat()},${bounds.southwest.lng()},${bounds.southwest.lat()}`, bounds.zoomLevel)
    }
  }, [filters])


  function sendToFlutter(payload: any) {
    window.FlutterBridge?.postMessage(JSON.stringify(payload));
    console.log("sendToFlutter", payload);
  }

  const retrieveSmallModel = async (userId: string, coordinates: string, zoomLevel: string) => {
    if (!filters.comm && !filters.my) {
      setSmallModelData(null)
      return;
    }

    const result = await findWorldMapModel(userId, coordinates, zoomLevel)
    console.log("retrieveSmallModel result", result);

    if (filters.comm && filters.my) {
      setSmallModelData(result.data);
    } else if (filters.comm) {
      const communityResult = result.data.filter(data => !data.isMyModel);
      console.log("filter comm result", communityResult);
      setSmallModelData(communityResult);
    } else if (filters.my) {
      const myMarkerResult = result.data.filter(data => data.isMyModel);
      console.log("filter myMarkerResult result", myMarkerResult);
      setSmallModelData(myMarkerResult);
    }
  }

  return <>
    {smallModelData && smallModelData.filter(data => !data.showVideo)
      .map((data, index) =>
        <MapImageMarker
          key={data.id}
          id={data.id}
          coordinate={data.coordinate as google.maps.LatLng}
          onClick={(sessionId: string) => {
            sendToFlutter({
              type: "marker_clicked",
              marker_type: data.isMyModel ? "my" : "comm",
              id: sessionId,
            });
          }}
          isMyMarker={data.isMyModel}
          refCallback={handleMarkerRef}
          imageSource={data.isMyModel ? "/assets/icon_my_marker.svg" : "/assets/icon_comm_marker.svg"} />
      )}

    {smallModelData && smallModelData.filter(data => data.showVideo)
      .map((data, index) =>
        <MapVideoMarker
          key={data.id}
          id={data.id}
          position={data.coordinate as google.maps.LatLng}
          videoSource={data.videoUrl}
          onClick={(sessionId: string) => {
            sendToFlutter({
              type: "marker_clicked",
              marker_type: data.isMyModel ? "my" : "comm",
              id: sessionId,
            });
          }}
        />)}

    {filters.venue && <VenueMarker refCallback={handleVenueMarkerRef} onClickMarker={(code: string, name: any) => {
      sendToFlutter({
        type: "marker_clicked",
        marker_type: "venue",
        venueCode: code,
        venueName: name
      });
    }} />}
  </>

}